import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FiDollarSign } from 'react-icons/fi';
import { useAllPayoutRequests } from '../../hooks/usePayouts';
import { callAdminApprovePayout, callAdminRejectPayout, getPaymentErrorMessage } from '../../hooks/useStripe';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import { StatusBadge } from '../common/StatusBadge';
import { formatRelativeTime } from '../../utils/formatters';
import { Price } from '../common/Price';

export default function AdminPaymentsOutgoing() {
  const { requests, loading } = useAllPayoutRequests();
  const [statusFilter, setStatusFilter] = useState('all');
  const rejectModal = useDisclosure();
  const toast = useToast();

  const [approvingId, setApprovingId] = useState(null);
  const [rejectingRequest, setRejectingRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [submittingReject, setSubmittingReject] = useState(false);

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return requests;
    return requests.filter((r) => r.status === statusFilter);
  }, [requests, statusFilter]);

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  const handleApprove = async (requestId) => {
    setApprovingId(requestId);
    try {
      await callAdminApprovePayout(requestId);
      toast({ title: 'Payout approved and transfer initiated', status: 'success', duration: 4000, isClosable: true });
    } catch (err) {
      toast({ title: 'Error', description: getPaymentErrorMessage(err), status: 'error', duration: 5000, isClosable: true });
    } finally {
      setApprovingId(null);
    }
  };

  const openRejectModal = (request) => {
    setRejectingRequest(request);
    setRejectReason('');
    rejectModal.onOpen();
  };

  const handleReject = async () => {
    if (!rejectingRequest) return;
    setSubmittingReject(true);
    try {
      await callAdminRejectPayout(rejectingRequest.id, rejectReason.trim());
      toast({ title: 'Payout request rejected', status: 'info', duration: 3000, isClosable: true });
      rejectModal.onClose();
      setRejectingRequest(null);
    } catch (err) {
      toast({ title: 'Error', description: getPaymentErrorMessage(err), status: 'error', duration: 5000, isClosable: true });
    } finally {
      setSubmittingReject(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading payout requests..." />;

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between" align="center">
        <Box>
          <Heading size="lg">Outgoing Payments</Heading>
          <Text color="gray.500" mt={1}>
            {requests.length} total requests{pendingCount > 0 && ` · ${pendingCount} pending`}
          </Text>
        </Box>
        <Select maxW="180px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </Select>
      </HStack>

      {filtered.length === 0 ? (
        <EmptyState
          title="No payout requests"
          description="Researcher payout requests will appear here."
          icon={FiDollarSign}
        />
      ) : (
        <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Researcher</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Requested</Th>
                  <Th>Details</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map((req) => (
                  <Tr key={req.id}>
                    <Td>
                      <Text
                        as={Link}
                        to={`/admin/users/${req.researcherUid}`}
                        fontSize="sm"
                        fontWeight="medium"
                        color="orange.500"
                        _hover={{ textDecoration: 'underline' }}
                      >
                        {req.researcherUsername}
                      </Text>
                      <Text fontSize="xs" color="gray.500">{req.researcherEmail}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" fontWeight="medium"><Price cents={req.amount} size="sm" /></Text>
                    </Td>
                    <Td><StatusBadge status={req.status} /></Td>
                    <Td>
                      <Text fontSize="xs" color="gray.500">{req.createdAt ? formatRelativeTime(req.createdAt) : ''}</Text>
                    </Td>
                    <Td maxW="200px">
                      {req.stripeTransferId && (
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>Transfer: {req.stripeTransferId}</Text>
                      )}
                      {req.rejectionReason && (
                        <Text fontSize="xs" color="red.500" noOfLines={2}>{req.rejectionReason}</Text>
                      )}
                      {req.error && (
                        <Text fontSize="xs" color="red.500" noOfLines={2}>{req.error}</Text>
                      )}
                    </Td>
                    <Td>
                      {req.status === 'pending' && (
                        <HStack spacing={2}>
                          <Button
                            size="xs"
                            colorScheme="green"
                            onClick={() => handleApprove(req.id)}
                            isLoading={approvingId === req.id}
                            loadingText="..."
                          >
                            Approve
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => openRejectModal(req)}
                          >
                            Reject
                          </Button>
                        </HStack>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}

      {/* Reject modal */}
      <Modal isOpen={rejectModal.isOpen} onClose={() => { rejectModal.onClose(); setRejectingRequest(null); }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reject Payout Request</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {rejectingRequest && (
                <Text fontSize="sm" color="gray.600">
                  Rejecting <strong><Price cents={rejectingRequest.amount} /></strong> payout for <strong>{rejectingRequest.researcherUsername}</strong>.
                  The requested amount will return to their available balance.
                </Text>
              )}
              <FormControl>
                <FormLabel fontSize="sm">Reason (optional)</FormLabel>
                <Textarea
                  placeholder="Why is this request being rejected?"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => { rejectModal.onClose(); setRejectingRequest(null); }}>Cancel</Button>
            <Button
              colorScheme="red"
              onClick={handleReject}
              isLoading={submittingReject}
            >
              Reject Payout
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
